'use client';

import { useState } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import DynamicIcon from 'app/(web)/components/common/DynamicIcon';
import { Product } from 'app/types/product';
import Carousel from 'designSystem/Carousel/Carousel';
import { Container, Flex } from 'designSystem/Layouts/Layouts';
import { Text, Title } from 'designSystem/Texts/Texts';
import { isEmpty } from 'lodash';

import { SURGERY_DATA } from './surgeryData';

export default function ProductSuggestions({ product }: { product: Product }) {
  const visibleSuggestions = isMobile ? 1 : isTablet ? 2 : 3;

  // Si el producto no tiene recomendaciones en la API, las de SURGERY_DATA.
  const recommendations = SURGERY_DATA.find(
    item => item.id.toUpperCase() === product.id.toUpperCase()
  )?.recommendations;

  const productPostTreatmentTips =
    product.postTreatmentInfo?.first24hTips?.concat(
      product.postTreatmentInfo.after24hTips
    );
  const preTreatmentTips = isEmpty(product.preTreatmentInfo?.tips)
    ? recommendations?.preTips
    : product.preTreatmentInfo.tips;
  const postTreatmentTips = isEmpty(productPostTreatmentTips)
    ? recommendations?.postTips
    : productPostTreatmentTips;

  const [activeSlider, setActiveSlider] = useState(
    !isEmpty(preTreatmentTips) || postTreatmentTips?.length == 0
      ? 'pre'
      : 'post'
  );

  if (isEmpty(preTreatmentTips) && isEmpty(postTreatmentTips)) {
    return <></>;
  }

  return (
    <div className="bg-derma-secondary300 pt-12 pb-8 md:py-16">
      <Container>
        <Title isAnimated size="2xl" className="text-center mb-8 md:mb-12">
          Recomendaciones médicas
        </Title>

        {!isEmpty(preTreatmentTips) && !isEmpty(postTreatmentTips) && (
          <Flex layout="col-center" className="mb-10">
            <ul className="inline-flex bg-derma-secondary500 p-1 rounded-full">
              <li
                id={'tmevent_click_treatment_info'}
                className={`transition-all text-xs font-medium py-3 px-4 rounded-full grow-0 cursor-pointer text-hg-secondary ${
                  activeSlider === 'pre'
                    ? 'bg-hg-secondary text-hg-secondary100'
                    : ''
                }`}
                onClick={() => setActiveSlider('pre')}
              >
                {recommendations?.preTitle ?? 'Pretratamiento'}
              </li>
              <li
                id={'tmevent_click_treatment_info'}
                className={`transition-all text-xs font-medium py-3 px-4 rounded-full grow-0 cursor-pointer ${
                  activeSlider === 'post'
                    ? 'bg-hg-secondary text-hg-secondary100'
                    : ''
                }`}
                onClick={() => setActiveSlider('post')}
              >
                {recommendations?.postTitle ?? 'Postratamiento'}
              </li>
            </ul>
          </Flex>
        )}

        <div className="relative overflow-hidden">
          <Carousel
            hasDots={isMobile}
            hasControls={!isMobile}
            controlstyles="px-4"
            className={`transition-all duration-500 ${activeSlider === 'pre' ? '' : '-translate-x-[120%] opacity-0 absolute'}`}
            isIntrinsicHeight
            visibleSlides={visibleSuggestions}
            infinite={false}
            sliderStyles="md:gap-10"
          >
            {preTreatmentTips &&
              activeSlider === 'pre' &&
              preTreatmentTips
                .sort((a, b) => a.priority - b.priority)
                .map(tip => {
                  return (
                    <Flex
                      key={tip.priority}
                      layout="col-center"
                      className="bg-derma-secondary400 p-8 rounded-2xl h-full mr-4 ml-4 preTripSlide justify-center"
                    >
                      <DynamicIcon
                        className="h-12 w-12 mb-6 text-hg-secondary"
                        name={`Svg${tip.icon}`}
                        family="suggestionIcons"
                      />
                      <Text className="text-center">{tip.details}</Text>
                    </Flex>
                  );
                })}
          </Carousel>

          <Carousel
            hasDots={isMobile}
            hasControls={!isMobile}
            controlstyles="px-4"
            className={`transition-all duration-500 ${activeSlider === 'post' ? '' : 'translate-x-[120%] opacity-0 absolute'}`}
            isIntrinsicHeight
            visibleSlides={visibleSuggestions}
            infinite={false}
            sliderStyles="md:gap-10"
          >
            {postTreatmentTips &&
              activeSlider === 'post' &&
              postTreatmentTips
                .sort((a, b) => a.priority - b.priority)
                .map(tip => {
                  return (
                    <Flex
                      key={tip.priority}
                      layout="col-center"
                      className="bg-derma-secondary400 p-8 rounded-2xl h-full mr-4 ml-4 postTripSlide justify-center"
                    >
                      <DynamicIcon
                        className="h-12 w-12 mb-6 text-hg-secondary"
                        name={`Svg${tip.icon}`}
                        family="suggestionIcons"
                      />
                      <Text className="text-center">{tip.details}</Text>
                    </Flex>
                  );
                })}
          </Carousel>
        </div>
      </Container>
    </div>
  );
}
